import React, { useState } from 'react';
import { Zap, Mail, Phone, MapPin, Linkedin, Twitter, Github, ArrowUp } from 'lucide-react';
import { companyData, contactData } from '../data/mock';
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

export const Footer = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNewsletterSignup = async () => {
    if (!newsletterEmail) {
      setSubscriptionMessage('Please enter your email address');
      return;
    }

    setIsSubscribing(true);
    setSubscriptionMessage('');

    try {
      await addDoc(collection(db, "newsletter_subscriptions"), {
        email: newsletterEmail,
        subscribed_at: serverTimestamp(),
      });

      setSubscriptionMessage("You've been subscribed to our newsletter.");
      setNewsletterEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setSubscriptionMessage('Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <footer className="relative overflow-hidden mt-20" style={{
      background: 'var(--bg-secondary)',
      borderTop: '1px solid var(--border-subtle)'
    }}>
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-10 left-10 w-32 h-32 rounded-full opacity-5 animate-float" style={{
          background: `radial-gradient(circle, var(--accent-primary), transparent)`,
          animationDelay: '1s'
        }}></div>
        <div className="absolute bottom-10 right-10 w-24 h-24 rounded-full opacity-10 animate-float" style={{
          background: `radial-gradient(circle, var(--accent-purple), transparent)`,
          animationDelay: '3s'
        }}></div>
      </div>

      <div className="container mx-auto px-6 py-16 relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            {/* Logo */}
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center animate-pulse-glow" style={{
                background: 'var(--accent-primary)'
              }}>
                <Zap className="w-6 h-6" style={{ color: 'var(--bg-primary)' }} />
              </div>
              <span className="h2" style={{ color: 'var(--text-primary)' }}>
                {companyData.name}
              </span>
            </div>

            {/* Description */}
            <p className="body-lg mb-6 max-w-md" style={{ color: 'var(--text-secondary)' }}>
              {companyData.description}
            </p>

            {/* Social Links */}
            <div className="flex space-x-4">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                style={{
                  background: 'var(--bg-tertiary)',
                  color: 'var(--text-muted)'
                }}
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                style={{
                  background: 'var(--bg-tertiary)',
                  color: 'var(--text-muted)'
                }}
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href="https://github.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                style={{
                  background: 'var(--bg-tertiary)',
                  color: 'var(--text-muted)'
                }}
              >
                <Github className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="h4 mb-6" style={{ color: 'var(--text-primary)' }}>
              Quick Links
            </h4>
            <div className="space-y-3">
              {[
                { label: 'Services', href: '#services' },
                { label: 'Features', href: '#features' },
                { label: 'Case Studies', href: '#case-studies' },
                { label: 'About', href: '#about' },
                { label: 'Team', href: '#team' },
                { label: 'Pricing', href: '#pricing' }
              ].map((link) => (
                <button
                  key={link.label}
                  onClick={() => scrollToSection(link.href)}
                  className="block body-md hover:text-white transition-colors duration-200"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="h4 mb-6" style={{ color: 'var(--text-primary)' }}>
              Contact Info
            </h4>
            <div className="space-y-4">
              {/* Email */}
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                <span className="body-sm" style={{ color: 'var(--text-secondary)' }}>
                  {contactData.email}
                </span>
              </div>

              {/* Phone */}
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                <span className="body-sm" style={{ color: 'var(--text-secondary)' }}>
                  {contactData.phone}
                </span>
              </div>

              {/* Address */}
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--accent-primary)' }} />
                <span className="body-sm" style={{ color: 'var(--text-secondary)' }}>
                  {contactData.address}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="border-t border-b py-8 mb-8" style={{ borderColor: 'var(--border-subtle)' }}>
          <div className="max-w-2xl mx-auto text-center">
            <h4 className="h3 mb-4" style={{ color: 'var(--text-primary)' }}>
              Stay Updated with Automation Trends
            </h4>
            <p className="body-md mb-6" style={{ color: 'var(--text-secondary)' }}>
              Get the latest insights, tips, and industry news delivered to your inbox monthly.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <input
                type="email"
                placeholder="Enter your email address"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="w-full sm:w-64 px-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none"
                style={{
                  background: 'var(--bg-primary)',
                  borderColor: 'var(--border-primary)',
                  color: 'var(--text-primary)'
                }}
                disabled={isSubscribing}
              />
              <button
                className={`btn-primary whitespace-nowrap ${isSubscribing ? 'opacity-50' : ''}`}
                onClick={handleNewsletterSignup}
                disabled={isSubscribing}
              >
                {isSubscribing ? 'Subscribing...' : 'Subscribe Now'}
              </button>
            </div>
            {subscriptionMessage && (
              <p className="mt-4 text-center body-sm" style={{ 
                color: subscriptionMessage.includes('Failed') ? '#ef4444' : 'var(--accent-primary)' 
              }}>
                {subscriptionMessage}
              </p>
            )}
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
          {/* Copyright */}
          <div className="body-sm" style={{ color: 'var(--text-muted)' }}>
            © {currentYear} {companyData.name}. All rights reserved.
          </div>

          {/* Legal Links */}
          <div className="flex items-center space-x-6">
            <button className="body-sm hover:text-white transition-colors duration-200" 
                    style={{ color: 'var(--text-muted)' }}>
              Privacy Policy
            </button>
            <button className="body-sm hover:text-white transition-colors duration-200" 
                    style={{ color: 'var(--text-muted)' }}>
              Terms of Service
            </button>
            <button className="body-sm hover:text-white transition-colors duration-200" 
                    style={{ color: 'var(--text-muted)' }}>
              Cookie Policy
            </button>
          </div>

          {/* Back to Top */}
          <button
            onClick={scrollToTop}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 animate-pulse-glow"
            style={{
              background: 'var(--accent-primary)',
              color: 'var(--bg-primary)'
            }}
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Additional Background Effects */}
      <div className="absolute bottom-0 left-0 right-0 h-px opacity-30" style={{
        background: `linear-gradient(90deg, transparent, var(--accent-primary), transparent)`
      }}></div>
    </footer>
  );
};